import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError, hasSessionMark, markSession } from "@/lib/api";
import { applyTheme, getTheme } from "@/lib/theme";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, obj = user
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    if (!hasSessionMark()) { setUser(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (data?.theme && data.theme !== getTheme()) applyTheme(data.theme);
    } catch {
      markSession(false);
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password, shop_slug = null) => {
    setError("");
    try {
      await api.post("/auth/login", { email, password, shop_slug: shop_slug || undefined });
      markSession(true);
      await refresh();
      return true;
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      throw new Error(msg);
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    markSession(false);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, error, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
